// tournamentControllerRules.js
export function computeTournamentButtonState(round = {}) {
  const stage = round.stage;
  const status = round.status;
  const period = round.period;
  const remaining = Number(round.finalistRoundsRemaining ?? 0);
  const hasTie = Boolean(round?.tieInfo?.hasTie);

  const allowed = {
    nextSeasonApprovalNeeded: false,

    canBeginTournamentYear: false,
    canOpenFinalistContestants: false,
    canCreateNextFinalsTournamentRound: false,
    canCreateNextChampionshipTournamentRound: false,

    canSetWarnClosesToday: false,
    canCloseTournament: false,

    canStartFinalistBattleRound: false,
    canStartChampionshipBattleRound: false,

    canSetWarnEndsToday: false,

    canDecideFinalistWinner: false,
    canDecideTieBreakerFinalistRound: false,

    canStartTieBreakerFinalistRound: false,
    canStartTieBreakerChampionshipRound: false,

    canDecideChampionWinner: false,
    canDecideChampionTieBreakerRound: false,

    canPurgePrepNextFinalistRound: false,
    canPurgeLastFinalistRoundAdvanceChampionshipRound: false,
    canPurgeChampionshipContestantsPrepBeginTournamentYr: false,
  };

  // -------------------------
  // GLOBAL / STAGE-AGNOSTIC
  // -------------------------
  // “Set Closes Today”: any stage, status open, period not already closesToday/nowClosed
  if (status === "open" && period !== "closesToday" && period !== "nowClosed") {
    allowed.canSetWarnClosesToday = true;
  }
  // “Close Tournament”: any stage, status open + period closesToday
  if (status === "open" && period === "closesToday") {
    allowed.canCloseTournament = true;
  }
  // “Set Ends Today”: finalist or championship, while battling
  if (
    (stage === "finalist" || stage === "championship") &&
    (status === "competing" || status === "tieBreaker") &&
    (period === "broadcastBattling" || period === "tieBreakerBattle")
  ) {
    allowed.canSetWarnEndsToday = true;
  }
  // “Create next round”: finalist after purgeCompleted
  if (stage === "finalist" && status === "purgeCompleted") {
    allowed.canCreateNextFinalsTournamentRound = true;
  }
  // “Create next round”: championship after purgeCompleted
  if (stage === "championship" && status === "purgeCompleted") {
    allowed.canCreateNextChampionshipTournamentRound = true;
  }
  // -------------------------
  // KICKOFF SEASON
  // -------------------------
  if (
    stage === "kickoffSeason" &&
    status === "closed" &&
    period === "opensToday"
  ) {
    allowed.canBeginTournamentYear = true;
  }
  // -------------------------
  // FINALIST FLOW
  // -------------------------

  // Open finalist contestants (entry window)
  if (stage === "finalist" && status === "closed" && period === "opensToday") {
    allowed.canOpenFinalistContestants = true;
  }
  // Start finalist battle (entry window ended)
  if (stage === "finalist" && status === "closed" && period === "nowClosed") {
    allowed.canStartFinalistBattleRound = true;
  }
  // Decide finalist winner (battle ended today)
  if (
    stage === "finalist" &&
    status === "competing" &&
    period === "endsToday"
  ) {
    allowed.canDecideFinalistWinner = true;
  }
  // Decide finalist tie-break winner (tie battle ended today)
  if (
    stage === "finalist" &&
    status === "tieBreaker" &&
    period === "endsToday"
  ) {
    allowed.canDecideTieBreakerFinalistRound = true;
  }
  // Start finalist tie-break battle
  // ✅ Backend declares tieBreaker, period stays ended
  if (
    stage === "finalist" &&
    status === "tieBreaker" &&
    period === "ended" &&
    hasTie === true
  ) {
    allowed.canStartTieBreakerFinalistRound = true;
  }
  // Purge after finalist decision complete
  if (
    stage === "finalist" &&
    status === "decisionComplete" &&
    period === "ended"
  ) {
    if (remaining > 0) {
      allowed.canPurgePrepNextFinalistRound = true;
    } else {
      allowed.canPurgeLastFinalistRoundAdvanceChampionshipRound = true;
    }
  }

  // -------------------------
  // CHAMPIONSHIP FLOW
  // -------------------------
  // Start championship battle
  if (
    stage === "championship" &&
    status === "closed" &&
    period === "nowClosed"
  ) {
    allowed.canStartChampionshipBattleRound = true;
  }
  // Decide champion winner
  if (
    stage === "championship" &&
    status === "competing" &&
    period === "endsToday"
  ) {
    allowed.canDecideChampionWinner = true;
  }
  // Decide championship tie-break winner
  if (
    stage === "championship" &&
    status === "tieBreaker" &&
    period === "endsToday"
  ) {
    allowed.canDecideChampionTieBreakerRound = true;
  }
  // Start championship tie-break battle
  if (
    stage === "championship" &&
    status === "tieBreaker" &&
    period === "ended" &&
    hasTie === true
  ) {
    allowed.canStartTieBreakerChampionshipRound = true;
  }
  // Purge championship contestants after decision complete
  if (
    stage === "championship" &&
    status === "decisionComplete" &&
    period === "ended"
  ) {
    allowed.canPurgeChampionshipContestantsPrepBeginTournamentYr = true;
  }

  // Next season approval gate
  if (
    stage === "championship" &&
    status === "purgeCompleted" &&
    period === "ended"
  ) {
    allowed.nextSeasonApprovalNeeded = true;
  }

  // Debug key (include tie flag so you can see it in the UI dump)
  const key = `${stage}:${status}:${period}:${hasTie ? "hasTie" : "noTie"}`;

  return { allowed, key };
}
